import React, { useState, useEffect } from "react";
import { Table, Container, Row, Col, Pagination } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Button, { BUTTON_TYPES } from '../../components/Button';
import SearchInput from '../../components/inputs/SearchInput';
import "../../styles/AdminPage.css";
import Page from "../../components/Page";
import CommitteeFilterModal from "../../components/modals/CommitteeFilterModal";
import CommitteeCreateModal from "../../components/modals/CommitteeCreateModal";
import CommitteeEditModal from "../../components/modals/CommitteeEditModal";
import { useLocation, useNavigate } from 'react-router-dom';
import FilterButton from "../../components/FilterButton";
import EditButton from "../../components/EditButton";
import FilterTag from "../../components/FilterTag";
import { useAdmin } from "./AdminContext";
import { getAllCommittees, getAllCommitteesIncludingArchived, restoreCommittee } from "../../api/admin/AdminDashboardAPI";
import CommitteeArchiveModal from "../../components/modals/CommitteeArchiveModal";
import toast from "react-hot-toast";

export default function CommitteeSearchPage() {
    // const [isArchive, setIsArchive] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);

    // TODO: change backend committee getAllCommittees() to get committeeHeads(WiCMember), and num of committee members
    const { roles, members, committees, setCommittees, updateCommittees, addCommittee, removeCommittee } = useAdmin();

    const correctRolesForCreateCommittee = ["ADMIN", "LOGHEAD"].some(r => roles.includes(r));

    const goToMembers = (committee, memberType) => {
        navigate('/admin/members', { state: { selectedCommittee: committee, selectedMemberType: memberType } });
    };

    const [selectedCommittee, setSelectedCommittee] = useState(null);

    const [activeModal, setActiveModal] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState({});
    const [modalFormData, setModalFormData] = useState({});

    const openModal = (modalType) => {
        setActiveModal(modalType);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleFilterApply = async (data) => {
        setAppliedFilters(data);
        if (data.isArchiveEnabled) {
            try {
                setLoading(true);
                const fresh = await getAllCommitteesIncludingArchived({ isArchived: data.isArchiveEnabled });
                setCommittees(fresh);
                search();
            } catch (err) {
                console.error("Failed to load committees:", err);
            } finally {
                setLoading(false);
            }

        } else {

            try {
                setLoading(true);
                const fresh = await getAllCommittees();
                setCommittees(fresh);
                search();
            } catch (err) {
                console.error("Failed to load committees:", err);
            } finally {
                setLoading(false);
            }

        }
        closeModal();
    };

    const defaultFilters = {
        activeMembersRange: { min: 0, max: 1000 },
        membersRange: { min: 0, max: 1000 },
        isArchiveEnabled: false
    };

    let filterTags = [];

    if (
        appliedFilters.isArchiveEnabled
    ) {
        // setIsArchive(true);
        filterTags.push(`Archived Committees Included`);
    }

    if (
        appliedFilters.activeMembersRange &&
        (appliedFilters.activeMembersRange.min !== defaultFilters.activeMembersRange.min ||
            appliedFilters.activeMembersRange.max !== defaultFilters.activeMembersRange.max)
    ) {
        filterTags.push(`Active Members: ${appliedFilters.activeMembersRange.min} - ${appliedFilters.activeMembersRange.max}`);
    }
    if (
        appliedFilters.membersRange &&
        (appliedFilters.membersRange.min !== defaultFilters.membersRange.min ||
            appliedFilters.membersRange.max !== defaultFilters.membersRange.max)
    ) {
        filterTags.push(`Members: ${appliedFilters.membersRange.min} - ${appliedFilters.membersRange.max}`);
    }

    const removeFilter = (filterName) => {
        if (filterName.startsWith("Active Members:")) {
            setAppliedFilters(prev => ({
                ...prev,
                activeMembersRange: defaultFilters.activeMembersRange
            }));
            setModalFormData(prev => ({
                ...prev,
                create: {
                    ...prev.create,
                    activeMembersRange: defaultFilters.activeMembersRange
                }
            }));
        } else if (filterName.startsWith("Members:")) {
            setAppliedFilters(prev => ({
                ...prev,
                membersRange: defaultFilters.membersRange
            }));
            setModalFormData(prev => ({
                ...prev,
                create: {
                    ...prev.create,
                    membersRange: defaultFilters.membersRange
                }
            }));
        } else if (filterName.startsWith("Archived")) {
            const updatedArchive = {
                ...appliedFilters,
                isArchiveEnabled: false
            };

            setAppliedFilters(updatedArchive);
            // setIsArchive(false);
            setModalFormData(prev => ({
                ...prev,
                create: {
                    ...prev.create,
                    isArchiveEnabled: defaultFilters.isArchiveEnabled
                }
            }));

            handleFilterApply(updatedArchive);
        }
    };

    const [displayCommittees, setDisplayCommittees] = useState(committees.sort((a, b) => a.committeeName.localeCompare(b.committeeName)));


    const search = () => {
        const lowerSearchQuery = searchQuery.toLowerCase().trim();
        let filteredCommittees;

        if (!lowerSearchQuery) {
            filteredCommittees = [...committees];
        } else {
            filteredCommittees = committees.filter(committee =>
                committee.committeeName.toLowerCase().includes(lowerSearchQuery)
            );
        }

        if (filterTags && filterTags.length > 0) {
            filteredCommittees = filteredCommittees.filter(committee => {
                return filterTags.every(filter => {
                    const filterLabel = filter.split(': ')[0];
                    switch (filterLabel) {
                        case 'Active Members':
                            const activeRangePart = filter.split(': ')[1];
                            const [activeMinStr, activeMaxStr] = activeRangePart.split(' - ');
                            const activeMin = parseInt(activeMinStr, 10);
                            const activeMax = parseInt(activeMaxStr, 10);
                            const activeMembers = committee.members.filter(m => m.member.active);
                            return activeMembers.length >= activeMin && activeMembers.length <= activeMax;
                        case 'Members':
                            const memberRangePart = filter.split(': ')[1];
                            const [memberMinStr, memberMaxStr] = memberRangePart.split(' - ');
                            const memberMin = parseInt(memberMinStr, 10);
                            const memberMax = parseInt(memberMaxStr, 10);
                            return committee.members.length >= memberMin && committee.members.length <= memberMax;
                        default:
                            return true;
                    }
                });
            });
        }

        const sortedCommittees = [...filteredCommittees].sort((a, b) => {
            return a.committeeName.localeCompare(b.committeeName);
        });

        const committeesWithActiveMembers = sortedCommittees.map(committee => {
            // const members = committee.members || []; // default to empty array
            const activeMembers = committee.members.filter(m => m.member.active);

            return {
                ...committee,
                activeMembers: activeMembers,
                activeMembersCount: activeMembers.length,
                membersCount: committee.members.length,
            };
        });

        setDisplayCommittees(committeesWithActiveMembers);
        setSortConfig({ key: 'committeeName', direction: 'ascending' });
        setCurrentPage(1);
        setExpandedRows([]);
    }

    useEffect(() => {
        search();
    }, [appliedFilters, committees]);

    const [expandedRows, setExpandedRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'committeeName', direction: 'ascending' });
    const [searchQuery, setSearchQuery] = useState('');
    const itemsPerPage = 25;

    const handleRowClick = (index) => {
        const isRowCurrentlyExpanded = expandedRows.includes(index);
        setExpandedRows(isRowCurrentlyExpanded
            ? expandedRows.filter(id => id !== index)
            : [...expandedRows, index]
        );
    };

    const handleSearchChange = (e) => setSearchQuery(e.target.value);
    const handleSearchClick = (e) => {
        e.preventDefault();
        search();
    };

    const indexOfLastCommittee = currentPage * itemsPerPage;
    const indexOfFirstCommittee = indexOfLastCommittee - itemsPerPage;
    const currentCommittee = displayCommittees.slice(indexOfFirstCommittee, indexOfLastCommittee);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        const sortedData = [...displayCommittees].sort((a, b) => {
            if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
            if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
            return 0;
        });
        setDisplayCommittees(sortedData);
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort />;
        return sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />;
    };

    const handleRestore = (committeeArch) => {
        toast.loading("Restoring Committee...");
        setTimeout(async () => {
            try {
                const response = await restoreCommittee(committeeArch);
                if (response.status === 200 || response.status === 201) {
                    toast.dismiss();
                    toast.success("Committee restored!");
                    const updatedCommittee = {
                        ...committeeArch,
                        archived: false
                    }
                    updateCommittees(updatedCommittee);
                } else {
                    toast.dismiss();
                    toast.error("Committee failed to restore. Status: " + response.status);
                }
            } catch (error) {
                toast.dismiss();
                console.error("Error restoring committee:", error);
                toast.error("Something went wrong. Please check your connection and try again.");
            }
        }, 300);
    }

    return (
        <Page>
            <Container id="committee-search-container" className="mt-3">
                <Row className="mb-3 align-items-center">
                    <Col>
                        <h2>Committees</h2>
                    </Col>
                    {(correctRolesForCreateCommittee) && (
                        <Col className="text-end">
                        <Button
                            id="create-committee-button"
                            type={BUTTON_TYPES.primary}
                            buttonText="Create Committee"
                            action={() => openModal('createCommittee')}
                        />
                        </Col>
                    )}
                </Row>

                <div className="d-flex flex-row mb-3 align-items-center w-100">
                    <form className="d-flex flex-row justify-content-between align-items-center flex-nowrap w-100" onSubmit={handleSearchClick} onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            search();
                        }
                    }}>
                        <div className="d-flex flex-row flex-nowrap flex-grow-1">
                            <SearchInput
                                id="search-bar"
                                placeholder="Search by Committee Name"
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />

                            <FilterButton
                                id="filter-button"
                                type={BUTTON_TYPES.secondary}
                                action={(committee) => {
                                    committee.preventDefault();
                                    openModal('filterCommittees');
                                }}
                            />
                        </div>

                        <div className="col-auto">
                            <Button
                                id="search-button"
                                type={BUTTON_TYPES.primary}
                                buttonText="Search"
                                action={(committee) => {
                                    committee.preventDefault();
                                    search()
                                }}
                            />
                        </div>
                    </form>
                </div>

                <div className="d-flex flex-row flex-wrap align-items-center mb-3" style={{ rowGap: '.5rem' }}>
                    <span className="me-3">Active Filters:</span>
                    {filterTags.map((tag, index) => (
                        <FilterTag key={index} filterName={tag} onRemove={() => removeFilter(tag)} />
                    ))}
                </div>

                {(loading) && (
                    <div>
                        Loading...
                    </div>
                )}


                {(!loading) && (
                <>
                
                <div>
                    <span className="text-start text-secondary mb-0">Total Committees Found: {displayCommittees.length}</span>
                </div>

                <Table striped bordered className="mt-0">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('committeeName')}>
                                Committee Name {getSortIcon('committeeName')}
                            </th>
                            <th onClick={() => handleSort('activeMembersCount')}>
                                Active Members {getSortIcon('activeMembersCount')}
                            </th>
                            <th onClick={() => handleSort('membersCount')}>
                                Members {getSortIcon('membersCount')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentCommittee.map((committee, index) => (
                            <React.Fragment key={index}>
                                <tr key={index} onClick={() => handleRowClick(index)}>
                                    <td>
                                        {committee.committeeName}
                                        {committee.archived && <span className="archived-red">{" (Archived)"}</span>}
                                    </td>
                                    <td>
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                goToMembers(committee, "Active Members");
                                            }}
                                            className="text-link"
                                        >
                                            {committee.members.filter(m => m.member.active).length}
                                        </span>
                                    </td>
                                    <td className="d-flex justify-content-between align-items-center">
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                goToMembers(committee, "Members");
                                            }}
                                            className="text-link"
                                        >
                                            {committee.membersCount}
                                        </span>
                                        <span className="expand-icon">
                                            {expandedRows.includes(index) ? <FaChevronUp /> : <FaChevronDown />}
                                        </span>
                                    </td>
                                </tr>
                                {expandedRows.includes(index) && (
                                    <tr key={`expanded-${index}`}>
                                        <td colSpan="3">
                                            <div className="d-flex flex-row justify-content-start mt-5 mb-5 ms-3">
                                                <div className="col-12"><b>Committee Head(s):</b> {committee?.heads
                                                    ?.map(c => c.member?.firstName + " " + c.member?.lastName)
                                                    .join(", ")}</div>
                                            </div>
                                            <div className="d-flex flex-row justify-content-between">
                                                <EditButton
                                                    id="edit-btn"
                                                    action={() => {
                                                        setSelectedCommittee(committee);
                                                        openModal('editCommittee');
                                                    }}
                                                />
                                                {!committee.archived && (
                                                    <Button
                                                        id="delete-button"
                                                        type={BUTTON_TYPES.danger}
                                                        buttonText="Archive"
                                                        action={() => {
                                                            setSelectedCommittee(committee);
                                                            openModal('archive');
                                                        }}
                                                    />
                                                )}
                                                {committee.archived && (
                                                    <Button
                                                        id="delete-button"
                                                        type={BUTTON_TYPES.success}
                                                        buttonText="Restore"
                                                        action={() => {
                                                            handleRestore(committee);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>

                <Pagination className="justify-content-center">
                    {Array.from({ length: Math.ceil(displayCommittees.length / itemsPerPage) }, (_, i) => (
                        <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => {
                            setCurrentPage(i + 1);
                            setExpandedRows([]);
                        }}>
                            {i + 1}
                        </Pagination.Item>
                    ))}
                </Pagination>
                </>
                )}
            </Container>

            {activeModal === 'filterCommittees' && (
                <CommitteeFilterModal
                    isOpen={true}
                    onClose={closeModal}
                    initialFormData={modalFormData.create || {}}
                    onFormDataChange={(data) =>
                        setModalFormData(prev => ({ ...prev, create: data }))
                    }
                    onApply={(data) => handleFilterApply(data)}
                />
            )}
            {activeModal === 'createCommittee' && (
                <CommitteeCreateModal
                    isOpen={true}
                    onClose={closeModal}
                    initialFormData={modalFormData.create || {}}
                    onFormDataChange={(data) =>
                        setModalFormData(prev => ({ ...prev, create: data }))
                    }
                    members={members}
                    addNewCommittee={(committee) => {
                        addCommittee(committee);
                        modalFormData.create = {};
                        handleFilterApply(appliedFilters);
                    }}
                />
            )}
            {activeModal === 'editCommittee' && (
                <CommitteeEditModal
                    isOpen={true}
                    onClose={closeModal}
                    committee={selectedCommittee}
                    initialFormData={modalFormData[selectedCommittee.id] || {}}
                    onFormDataChange={(updatedValues) =>
                        setModalFormData((prev) => ({
                            ...prev,
                            [selectedCommittee.id]: updatedValues
                        }))
                    }
                    members={members}
                    updateCommittee={(displayCommittees) => updateCommittees(displayCommittees)}
                />
            )}
            {activeModal === 'archive' && (
                <CommitteeArchiveModal
                    isOpen={true}
                    onClose={closeModal}
                    committee={selectedCommittee}
                    updateCommitteesAfterRestoreOrArchive={(committee) => {
                        if (appliedFilters.isArchiveEnabled) {
                            updateCommittees(committee);
                            handleFilterApply(appliedFilters);
                        } else {
                            removeCommittee(committee);
                        }
                    }}
                />
            )}
        </Page>
    );
}